import type {
  ChangeMark,
  ExtraRowCells,
  FieldCorrection,
  FillOwners,
  FieldValue,
  MatrixAnswers,
  ResponseChanges,
  ResponseCorrections,
  ResponseMarks,
  TableAnswers,
  TableChanges,
} from '../model/types';
import { isEmptyValue } from './definitions/shared';
import { hasStoredValue, sameFieldValue } from './field-walk';

type ExtraRow = Record<number, FieldValue>;

interface Written {
  value: FieldValue;
  by: number;
}

interface WrittenState {
  values: Record<string, Written>;
  rows: Record<string, Array<Record<number, Written>>>;
}

function hasCell<T>(row: Record<number, T>, column: number): boolean {
  return Object.prototype.hasOwnProperty.call(row, column);
}

function changedValues(previous: MatrixAnswers | null, next: MatrixAnswers): Record<string, FieldValue> {
  const before = previous?.values ?? {};
  const values: Record<string, FieldValue> = {};

  for (const fieldId of Object.keys(before)) {
    if (!hasStoredValue(next.values, fieldId)) {
      throw new Error(`Field ${fieldId} left the answers: a response only adds or changes values.`);
    }
  }
  for (const [fieldId, value] of Object.entries(next.values)) {
    if (!hasStoredValue(before, fieldId) || !sameFieldValue(before[fieldId], value)) values[fieldId] = value;
  }

  return values;
}

function tableIdsOf(previous: MatrixAnswers | null, next: MatrixAnswers): Set<string> {
  return new Set([...Object.keys(previous?.tables ?? {}), ...Object.keys(next.tables)]);
}

function editedCells(tableId: string, rowIndex: number, before: ExtraRow, after: ExtraRow): ExtraRow {
  const cells: ExtraRow = {};

  for (const column of Object.keys(before).map(Number)) {
    if (!hasCell(after, column)) {
      throw new Error(`Table ${tableId} row ${rowIndex} lost column ${column}: a response clears a cell, never removes it.`);
    }
  }
  for (const [key, value] of Object.entries(after)) {
    const column = Number(key);
    if (!hasCell(before, column) || !sameFieldValue(before[column], value)) cells[column] = value;
  }

  return cells;
}

function correctedRows(previous: MatrixAnswers | null, next: MatrixAnswers): Record<string, TableChanges> {
  const tables: Record<string, TableChanges> = {};

  for (const tableId of tableIdsOf(previous, next)) {
    const historical = previous?.tables[tableId]?.extraRows ?? [];
    const rows = next.tables[tableId]?.extraRows ?? [];
    if (rows.length < historical.length) {
      throw new Error(`Table ${tableId} lost a historical row: a response changes cells, never removes rows.`);
    }

    const editedRows: ExtraRowCells<FieldValue> = {};
    historical.forEach((row, index) => {
      const cells = editedCells(tableId, index, row, rows[index]);
      if (Object.keys(cells).length > 0) editedRows[index] = cells;
    });

    const entry: TableChanges = { extraRows: rows.slice(historical.length) };
    if (Object.keys(editedRows).length > 0) entry.editedRows = editedRows;
    if (entry.extraRows.length > 0 || entry.editedRows) tables[tableId] = entry;
  }

  return tables;
}

function writtenCells(row: ExtraRow, by: number): Record<number, Written> {
  const cells: Record<number, Written> = {};
  for (const [column, value] of Object.entries(row)) cells[Number(column)] = { value, by };
  return cells;
}

// Every write replaces the Written entry instead of mutating it, so a mark taken before the write
// keeps pointing at the value it was measured against.
function applyChange(state: WrittenState, change: ResponseChanges, by: number): void {
  for (const [fieldId, value] of Object.entries(change.values)) state.values[fieldId] = { value, by };
  for (const [tableId, entry] of Object.entries(change.tables)) {
    const rows = state.rows[tableId] ?? [];
    for (const [rowKey, cells] of Object.entries(entry.editedRows ?? {})) {
      const row = rows[Number(rowKey)];
      if (!row) throw new Error(`Table ${tableId} edited row ${rowKey} before any response appended it.`);
      Object.assign(row, writtenCells(cells, by));
    }
    rows.push(...entry.extraRows.map((row) => writtenCells(row, by)));
    state.rows[tableId] = rows;
  }
}

// Response numbers start at 1 and grow by one per record, so the position in the ordered list is
// the response number. A gap would shift every `fromResponse` without any error.
function writtenState(changes: readonly ResponseChanges[]): WrittenState {
  const state: WrittenState = { values: {}, rows: {} };
  changes.forEach((change, index) => applyChange(state, change, index + 1));
  return state;
}

function photoOf(matrixId: number, state: WrittenState): MatrixAnswers {
  const values: Record<string, FieldValue> = {};
  for (const [fieldId, written] of Object.entries(state.values)) values[fieldId] = written.value;

  const tables: Record<string, TableAnswers> = {};
  for (const [tableId, rows] of Object.entries(state.rows)) {
    tables[tableId] = {
      extraRows: rows.map((row) => {
        const cells: ExtraRow = {};
        for (const [column, written] of Object.entries(row)) cells[Number(column)] = written.value;
        return cells;
      }),
    };
  }

  return { version: 1, matrixId, values, tables };
}

// Empty is the test validate uses, so a whitespace-only text that passed as blank counts as empty
// on both sides and turning it into an answer is a fill, not a correction.
function markOf(written: Written | undefined, value: FieldValue): ChangeMark | null {
  if (written === undefined || isEmptyValue(written.value)) return isEmptyValue(value) ? null : { kind: 'filled' };
  if (sameFieldValue(written.value, value)) return null;
  return { kind: 'corrected', before: written.value, fromResponse: written.by };
}

function cellMarksOf(
  rows: ReadonlyArray<Record<number, Written>>,
  editedRows: ExtraRowCells<FieldValue>
): ExtraRowCells<ChangeMark> {
  const cells: ExtraRowCells<ChangeMark> = {};

  for (const [rowKey, edited] of Object.entries(editedRows)) {
    const rowIndex = Number(rowKey);
    for (const [columnKey, value] of Object.entries(edited)) {
      const column = Number(columnKey);
      const mark = markOf(rows[rowIndex]?.[column], value);
      if (mark) cells[rowIndex] = { ...cells[rowIndex], [column]: mark };
    }
  }

  return cells;
}

function marksAgainst(state: WrittenState, change: ResponseChanges): ResponseMarks {
  const marks: ResponseMarks = { values: {}, cells: {}, newRows: {} };

  for (const [fieldId, value] of Object.entries(change.values)) {
    const mark = markOf(state.values[fieldId], value);
    if (mark) marks.values[fieldId] = mark;
  }
  for (const [tableId, entry] of Object.entries(change.tables)) {
    const rows = state.rows[tableId] ?? [];
    const cells = cellMarksOf(rows, entry.editedRows ?? {});
    if (Object.keys(cells).length > 0) marks.cells[tableId] = cells;
    if (entry.extraRows.length > 0) {
      marks.newRows[tableId] = entry.extraRows.map((_, offset) => rows.length + offset);
    }
  }

  return marks;
}

function correctionOf(mark: ChangeMark): FieldCorrection | null {
  return mark.kind === 'corrected' ? { before: mark.before, fromResponse: mark.fromResponse } : null;
}

function cellCorrectionsOf(rows: ExtraRowCells<ChangeMark>): ExtraRowCells<FieldCorrection> {
  const cells: ExtraRowCells<FieldCorrection> = {};

  for (const [rowKey, columns] of Object.entries(rows)) {
    for (const [columnKey, mark] of Object.entries(columns)) {
      const correction = correctionOf(mark);
      if (correction) cells[Number(rowKey)] = { ...cells[Number(rowKey)], [Number(columnKey)]: correction };
    }
  }

  return cells;
}

function correctionsOf(marks: ResponseMarks): ResponseCorrections | null {
  const corrections: ResponseCorrections = { values: {}, cells: {} };

  for (const [fieldId, mark] of Object.entries(marks.values)) {
    const correction = correctionOf(mark);
    if (correction) corrections.values[fieldId] = correction;
  }
  for (const [tableId, rows] of Object.entries(marks.cells)) {
    const cells = cellCorrectionsOf(rows);
    if (Object.keys(cells).length > 0) corrections.cells[tableId] = cells;
  }

  return isEmptyCorrections(corrections) ? null : corrections;
}

function isEmptyCorrections(corrections: ResponseCorrections): boolean {
  return Object.keys(corrections.values).length === 0 && Object.keys(corrections.cells).length === 0;
}

// A value that leaves, a historical row that goes or a cell that leaves its row throws instead of
// being dropped: the changes only add and overwrite, so the sum could never rebuild that photo.
// Which historical cells may change is the acceptance's call, not this diff's.
export function changesBetween(previous: MatrixAnswers | null, next: MatrixAnswers): ResponseChanges {
  return {
    version: 1,
    matrixId: next.matrixId,
    values: changedValues(previous, next),
    tables: correctedRows(previous, next),
  };
}

// The corrections are rebuilt here from the history, never read from `next`: the old value and the
// response it came from are the audit trail, so a caller must not be able to write them.
export function correctionChanges(history: readonly ResponseChanges[], next: MatrixAnswers): ResponseChanges {
  const state = writtenState(history);
  const previous = photoOf(next.matrixId, state);
  const changes: ResponseChanges = {
    version: 1,
    matrixId: next.matrixId,
    values: changedValues(previous, next),
    tables: correctedRows(previous, next),
  };
  const corrections = correctionsOf(marksAgainst(state, changes));
  return corrections ? { ...changes, corrections } : changes;
}

// The owner of a field is the response that filled it while it was empty; a later change by its
// owner or by a correction keeps the owner, and clearing the field frees it for anyone. A row
// belongs to the response that appended it, whoever edits its cells later.
export function fillOwners(changes: readonly ResponseChanges[]): FillOwners {
  const owners: FillOwners = { values: {}, rows: {} };
  const current: Record<string, FieldValue> = {};

  changes.forEach((change, index) => {
    const number = index + 1;
    for (const [fieldId, value] of Object.entries(change.values)) {
      if (isEmptyValue(value)) delete owners.values[fieldId];
      else if (current[fieldId] === undefined || isEmptyValue(current[fieldId])) owners.values[fieldId] = number;
      current[fieldId] = value;
    }
    for (const [tableId, entry] of Object.entries(change.tables)) {
      owners.rows[tableId] = [...(owners.rows[tableId] ?? []), ...entry.extraRows.map(() => number)];
    }
  });

  return owners;
}

export function sumChanges(matrixId: number, changes: readonly ResponseChanges[]): MatrixAnswers {
  return photoOf(matrixId, writtenState(changes));
}

export function responseMarks(changes: readonly ResponseChanges[], number: number): ResponseMarks {
  if (!Number.isInteger(number) || number < 1 || number > changes.length) {
    throw new RangeError(`Response ${number} is outside the ${changes.length} responses given.`);
  }
  return marksAgainst(writtenState(changes.slice(0, number - 1)), changes[number - 1]);
}

// One pass over the history: the marks of response N are measured before N is folded in, so the
// result equals calling responseMarks for every number without summing each prefix again.
export function responseMarksTimeline(changes: readonly ResponseChanges[]): ResponseMarks[] {
  const state: WrittenState = { values: {}, rows: {} };
  return changes.map((change, index) => {
    const marks = marksAgainst(state, change);
    applyChange(state, change, index + 1);
    return marks;
  });
}

// The photo after each response, in order, folded once. A photo the caller keeps does not move when
// the fold goes on, because each one gets its own value and row objects.
export function* photoTimeline(matrixId: number, changes: readonly ResponseChanges[]): Generator<MatrixAnswers> {
  const state: WrittenState = { values: {}, rows: {} };
  for (const [index, change] of changes.entries()) {
    applyChange(state, change, index + 1);
    yield photoOf(matrixId, state);
  }
}

export function hasCorrections(changes: ResponseChanges): boolean {
  return changes.corrections !== undefined && !isEmptyCorrections(changes.corrections);
}
